import {
  asRecord,
  asArray,
  cleanRecord,
  cloneDesignerDocument as cloneDocumentSnapshot,
  cloneValue,
  DesignerColumn,
  DesignerComponent,
  DesignerDocument,
  DesignerField,
  DesignerPageOrientation,
  DesignerPageSize,
  DesignerSelection,
  findBand,
  findComponent,
  JsonRecord,
  primaryComponent,
} from "./reportDesignerModel";
import { assertReportAdmission } from "../nativeConditions.mjs";
import { assertDesignerCommandAdmission, inheritInternalSession, internalSessionOf } from "./internalConditionalSession";
import { mutateConditionalRule, reidentifyConditionalCopy, type ConditionalRuleAction } from "./conditionalAuthoring";
import { analyzeFormula, formulaFields } from "./formulaAuthoring";
import { formulaColumn, formulaEditingContext, formulaTextTarget, isFormulaTarget, isInternalFormulaSession } from "./internalFormulaSession";
import { inspectTypedExpressionStructure, TYPED_EXPRESSIONS_CAPABILITY } from "../nativeExpression.mjs";
import { compileReportParameters, REPORT_PARAMETERS_CAPABILITY, type PresentationFilter, type ReportParameter } from "../nativeParameters.mjs";
import { parameterDependencies } from "./parameterAuthoring";

export type DesignerCommandResult = {
  document: DesignerDocument;
  selection?: DesignerSelection;
};

type NativeBandRole = "reportHeader" | "pageHeader" | "detail" | "reportFooter" | "pageFooter";
type NativeElementPatch = {
  text?: string;
  binding?: string;
  asset?: string;
  fit?: "contain" | "cover" | "stretch";
  lockAspectRatio?: boolean;
  format?: string;
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  color?: string;
  backgroundColor?: string;
  horizontalAlign?: "left" | "center" | "right";
  verticalAlign?: "top" | "middle" | "bottom";
  lineHeight?: number;
  padding?: number;
  gap?: number;
  layoutMode?: "absolute" | "vertical" | "columns";
  borderColor?: string;
  borderWidth?: number;
};
type NativeTableColumnPatch = {
  label?: string;
  binding?: string;
  expr?: JsonRecord | null;
  calculation?: JsonRecord | null;
  format?: string;
  width?: number | string;
  horizontalAlign?: "left" | "center" | "right";
  headerHorizontalAlign?: "left" | "center" | "right";
  visible?: boolean;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
};
type NativeTablePatch = {
  dataset?: string;
  x?: number;
  y?: number;
  width?: number;
  fontSize?: number;
  headerBackgroundColor?: string;
  footerBackgroundColor?: string;
  borderColor?: string;
  repeatHeader?: boolean;
  footerEnabled?: boolean;
  rowPadding?: number;
};
type NativeTableSortPatch = {
  field?: string;
  direction?: "asc" | "desc";
  type?: "auto" | "date" | "number" | "string";
  nulls?: "first" | "last";
};
type NativeTableGroupPatch = {
  enabled?: boolean;
  field?: string;
  sort?: "asc" | "desc";
  headerVisible?: boolean;
  footerVisible?: boolean;
  headerLabel?: string;
  spacingAfter?: number;
  pageBreakBefore?: boolean;
  repeatHeaderOnContinuation?: boolean;
  keepHeaderWithFirstRow?: boolean;
  keepFooterWithLastRow?: boolean;
  showGroupedFieldInDetail?: boolean;
};
type NativeTableSummaryPatch = {
  target: "groupFooter" | "tableFooter";
  enabled?: boolean;
  label?: string;
  labelColSpan?: number;
  rowHeight?: number;
  fontSize?: number;
  color?: string;
  backgroundColor?: string;
  bold?: boolean;
  aggregateIndex?: number;
  addAggregate?: boolean;
  removeAggregateIndex?: number;
  aggregate?: {
    fn?: "sum" | "count" | "min" | "max" | "avg";
    field?: string;
    scope?: "group" | "report";
    columnId?: string;
    format?: string;
    horizontalAlign?: "left" | "center" | "right";
  };
};
type NativeTableSelection = Extract<DesignerSelection, { type: "nativeElement" | "nativeTableColumn" | "nativeTableGroup" }>;
type NativeElementBounds = { x: number; y: number; width: number; height: number };
type NativeAlignment = "left" | "right" | "hCenter" | "top" | "bottom" | "vCenter" | "distributeH" | "distributeV";
const NATIVE_TABLE_MIN_COLUMN_WIDTH = 24;
const NATIVE_TABLE_WIDTH_TOLERANCE = 0.01;
const NATIVE_INSERT_PADDING = 12;
const NATIVE_INSERT_GAP = 8;
const NATIVE_PAGE_SIZES: Record<string, { width: number; height: number }> = {
  Letter: { width: 612, height: 792 },
  Legal: { width: 612, height: 1008 },
  A4: { width: 595.28, height: 841.89 },
};

export function createNativeDocument(seed?: DesignerDocument): DesignerCommandResult {
  if (seed) assertReportAdmission(seed, "edit");
  const timestamp = Date.now();
  const document: DesignerDocument = {
    schemaVersion: "reports-v3.1",
    sourceSchemaVersion: "reports-v3.1",
    definitionId: `native-report-${timestamp}`,
    definitionVersion: 1,
    reportKey: seed?.reportKey || "invoice",
    reportType: seed?.reportType || "invoice",
    scope: seed?.scope || "tenant",
    name: "Native Report",
    roles: [],
    requiredFilters: seed?.requiredFilters || {},
    capabilities: { preview: true, pdf: true, excel: false },
    data: seed?.data || { datasets: [] },
    metadata: { reportName: "Native Report" },
    assets: seed?.assets || {},
    datasets: seed?.datasets || {},
    page: { size: "letter", orientation: "portrait", margins: { top: 36, right: 36, bottom: 36, left: 36 } },
    styles: {},
    outputs: { pdf: { renderer: "document" } },
    bands: [],
    renderer: {
      topLevel: {},
      layout: {
        version: "reports-v3.1",
        page: { size: "Letter", orientation: "portrait", margins: { top: 36, right: 36, bottom: 36, left: 36 } },
        outputs: { pdf: { renderer: "document" } },
        bands: [],
      },
    },
  };
  return { document, selection: { type: "report" } };
}

export function addNativeBand(document: DesignerDocument, role: NativeBandRole): DesignerCommandResult {
  const id = uniqueNativeBandId(nativeBands(document), role);
  return mutateDocument(document, (draft) => {
    const bands = nativeBands(draft);
    bands.push({ id, role, height: role === "detail" ? 64 : 48, elements: [] });
  }, { type: "band", bandId: id });
}

export function updateNativeBand(document: DesignerDocument, bandId: string, key: "id" | "height" | "sizing", value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, bandId);
    if (!band) return;
    if (key === "id") {
      const nextId = String(value || band.id).trim() || band.id;
      band.id = nextId;
      return;
    }

    if (key === "sizing") {
      if (value === "flow") band.sizing = "flow";
      else delete band.sizing;
      return;
    }
    band.height = Math.max(12, Number(value) || Number(band.height) || 48);
  }, { type: "band", bandId: key === "id" ? String(value || bandId) : bandId });
}

export function resizeNativeBand(document: DesignerDocument, bandId: string, height: number): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, bandId);
    if (!band) return;
    band.height = Math.max(12, Number(height) || Number(band.height) || 48);
  }, { type: "band", bandId });
}

export function addNativeTextElement(document: DesignerDocument, bandId: string, at: { x: number; y: number } | undefined = { x: 24, y: 12 }, parentElementId?: string): DesignerCommandResult {
  const elementId = uniqueNativeElementId(findNativeBand(document, bandId) || {}, "text");
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, bandId);
    if (!band) return;
    const parent = parentElementId ? findNativeElement(draft, bandId, parentElementId) : undefined;
    const targetElements = parent && parent.type === "container" ? nativeElements(parent) : nativeElements(band);
    const placement = resolveNativeInsertionPlacement(draft, band, parent, at, { width: 180, height: 24 });
    targetElements.push({
      id: elementId,
      type: "text",
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
      text: "Text",
      style: { fontSize: 12, bold: false, color: "#000000", horizontalAlign: "left", verticalAlign: "top", lineHeight: 1.2 },
    });
    expandNativeBandForInsertion(draft, band, parent, placement);
  }, { type: "nativeElement", bandId, elementIds: [elementId] });
}

export function addNativeFieldElement(document: DesignerDocument, bandId: string, datasetName: string, fieldKey: string, at?: { x: number; y: number }, parentElementId?: string): DesignerCommandResult {
  const elementId = uniqueNativeElementId(findNativeBand(document, bandId) || {}, fieldKey || "field");
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, bandId);
    if (!band) return;
    if (band.role === "detail" && datasetName) band.dataset = band.dataset || datasetName;
    const binding = band.role === "detail" ? fieldKey : datasetName ? `${datasetName}.${fieldKey}` : fieldKey;
    const parent = parentElementId ? findNativeElement(draft, bandId, parentElementId) : undefined;
    const targetElements = parent && parent.type === "container" ? nativeElements(parent) : nativeElements(band);
    const placement = resolveNativeInsertionPlacement(draft, band, parent, at, { width: 180, height: 18 });
    targetElements.push({
      id: elementId,
      type: "field",
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
      binding,
      style: { fontSize: 10, color: "#000000", horizontalAlign: "left", verticalAlign: "top", lineHeight: 1.2 },
    });
    expandNativeBandForInsertion(draft, band, parent, placement);
  }, { type: "nativeElement", bandId, elementIds: [elementId] });
}

export function addNativeContainerElement(document: DesignerDocument, bandId: string, parentElementId?: string, at: { x: number; y: number } = { x: 24, y: 12 }): DesignerCommandResult {
  const band = findNativeBand(document, bandId);
  const elementId = uniqueNativeElementId(band || {}, "container");
  return mutateDocument(document, (draft) => {
    const draftBand = findNativeBand(draft, bandId);
    if (!draftBand) return;
    const parent = parentElementId ? findNativeElement(draft, bandId, parentElementId) : undefined;
    const targetElements = parent && parent.type === "container" ? nativeElements(parent) : nativeElements(draftBand);
    targetElements.push({
      id: elementId,
      type: "container",
      x: Math.max(0, at.x),
      y: Math.max(0, at.y),
      width: parent ? 180 : 360,
      height: parent ? 84 : 120,
      padding: 8,
      gap: 8,
      layout: { mode: "absolute", gap: 8 },
      style: { borderColor: "#d8e1ec", borderWidth: 1, backgroundColor: "#ffffff" },
      elements: [],
    });
  }, { type: "nativeElement", bandId, elementIds: [elementId] });
}

export function addNativeImageElement(document: DesignerDocument, bandId: string, asset = "companyLogo", at: { x: number; y: number } | undefined = { x: 24, y: 12 }, parentElementId?: string): DesignerCommandResult {
  const band = findNativeBand(document, bandId);
  const elementId = uniqueNativeElementId(band || {}, "image");
  return mutateDocument(document, (draft) => {
    const draftBand = findNativeBand(draft, bandId);
    if (!draftBand) return;
    const parent = parentElementId ? findNativeElement(draft, bandId, parentElementId) : undefined;
    const targetElements = parent && parent.type === "container" ? nativeElements(parent) : nativeElements(draftBand);
    const placement = resolveNativeInsertionPlacement(draft, draftBand, parent, at, { width: 96, height: 48 });
    targetElements.push({
      id: elementId,
      type: "image",
      x: placement.x,
      y: placement.y,
      width: placement.width,
      height: placement.height,
      asset,
      fit: "contain",
      lockAspectRatio: true,
      style: { borderColor: "#d8e1ec", borderWidth: 1, backgroundColor: "#ffffff" },
    });
    expandNativeBandForInsertion(draft, draftBand, parent, placement);
  }, { type: "nativeElement", bandId, elementIds: [elementId] });
}

export function addNativeTableElement(document: DesignerDocument, bandId: string, datasetName = "", fields: string[] = [], at?: { x: number; y: number }): DesignerCommandResult {
  const band = findNativeBand(document, bandId);
  const tableId = uniqueNativeElementId(band || {}, "table");
  return mutateDocument(document, (draft) => {
    const draftBand = findNativeBand(draft, bandId);
    if (!draftBand) return;
    const elements = nativeElements(draftBand);
    const maxBottom = elements.reduce((max, element) => Math.max(max, Number(element.y || 0) + Number(element.height || 0)), 0);
    const x = Math.max(0, at?.x ?? 0);
    const y = Math.max(0, at?.y ?? (elements.length ? maxBottom + NATIVE_INSERT_GAP : 0));
    draftBand.sizing = "flow";
    draftBand.height = Math.max(Number(draftBand.height || 64), y + 72);
    elements.push({
      id: tableId,
      type: "table",
      x,
      y,
      width: Math.max(120, nativeBandContentWidth(draft) - x),
      height: 48,
      dataset: datasetName || undefined,
      style: { fontSize: 9, lineHeight: 1.25, borderColor: "#808080", headerBackgroundColor: "#f0f5ff", footerBackgroundColor: "#f8fbff" },
      header: { repeat: true, style: { bold: true } },
      row: { padding: 4 },
      columns: fields.slice(0, 4).map((field) => nativeTableColumn(field, field)),
      footer: { enabled: false, cells: [] },
    });
  }, { type: "nativeElement", bandId, elementIds: [tableId] });
}

export function updateNativeTable(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, patch: NativeTablePatch): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.elementIds[0]);
    if (!table || table.type !== "table") return;
    if (patch.dataset !== undefined) table.dataset = patch.dataset || undefined;
    if (patch.x !== undefined) table.x = Math.max(0, Number(patch.x) || 0);
    if (patch.y !== undefined) table.y = Math.max(0, Number(patch.y) || 0);
    if (patch.width !== undefined) {
      resizeNativeTableToWidth(draft, table, Number(patch.width) || Number(table.width) || 0);
    }
    const style = asRecord(table.style);
    for (const key of ["fontSize", "headerBackgroundColor", "footerBackgroundColor", "borderColor"] as const) {
      if (patch[key] !== undefined) style[key] = patch[key];
    }
    table.style = cleanRecord(style);
    if (patch.repeatHeader !== undefined) {
      table.header = { ...asRecord(table.header), repeat: patch.repeatHeader };
      table.pagination = { ...asRecord(table.pagination), repeatHeader: patch.repeatHeader };
    }
    if (patch.rowPadding !== undefined) {
      table.row = { ...asRecord(table.row), padding: Math.max(0, Number(patch.rowPadding) || 0) };
    }
    if (patch.footerEnabled !== undefined) {
      table.footer = patch.footerEnabled
        ? ensureNativeTableFooter(table)
        : { ...asRecord(table.footer), enabled: false, cells: [] };
    }
  }, selection);
}

export function addNativeTableColumn(document: DesignerDocument, bandId: string, tableId: string, fieldKey: string, datasetName?: string): DesignerCommandResult {
  const nextColumnId = uniqueNativeTableColumnId(findNativeElement(document, bandId, tableId), fieldKey);
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, bandId, tableId);
    if (!table || table.type !== "table") return;
    if (datasetName && !table.dataset) table.dataset = datasetName;
    const columns = nativeTableColumns(table);
    columns.push(nativeTableColumn(nextColumnId, fieldKey));
    normalizeNativeTableColumnWidths(table, Number(table.width || 0));
    if (asRecord(table.footer).enabled !== false && asArray(asRecord(table.footer).cells).length) table.footer = normalizeNativeTableFooter(table);
  }, { type: "nativeTableColumn", bandId, tableId, columnId: nextColumnId });
}

export function addNativeCalculatedTableColumn(
  document: DesignerDocument,
  bandId: string,
  tableId: string,
  options: { label?: string; expr?: JsonRecord; format?: string; width?: number | string } = {},
): DesignerCommandResult {
  const table = findNativeElement(document, bandId, tableId);
  const nextColumnId = uniqueNativeTableColumnId(table, options.label || "calculated-field");
  return mutateDocument(document, (draft) => {
    const draftTable = findNativeElement(draft, bandId, tableId);
    if (!draftTable || draftTable.type !== "table") return;
    const columns = nativeTableColumns(draftTable);
    columns.push({
      id: nextColumnId,
      label: options.label || "Calculated Field",
      expr: cloneValue(options.expr || { op: "mul", left: { const: 0 }, right: { const: 0 } }),
      width: options.width || 90,
      format: options.format || "number",
      horizontalAlign: "right",
    });
    normalizeNativeTableColumnWidths(draftTable, Number(draftTable.width || 0));
    if (asRecord(draftTable.footer).enabled !== false && asArray(asRecord(draftTable.footer).cells).length) draftTable.footer = normalizeNativeTableFooter(draftTable);
  }, { type: "nativeTableColumn", bandId, tableId, columnId: nextColumnId });
}

export function updateNativeTableColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, patch: NativeTableColumnPatch): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const column = findNativeTableColumn(draft, selection.bandId, selection.tableId, selection.columnId);
    if (!column) return;
    for (const key of ["label", "format", "width", "horizontalAlign", "headerHorizontalAlign"] as const) {
      if (patch[key] !== undefined) column[key] = patch[key] || undefined;
    }
    if (patch.binding !== undefined) {
      column.binding = patch.binding || undefined;
      column.key = undefined;
      delete column.expr;
      delete column.calculation;
    }
    if ("expr" in patch) {
      if (patch.expr) column.expr = cloneValue(patch.expr);
      else delete column.expr;
      delete column.calculation;
      if (patch.expr) {
        delete column.binding;
        delete column.key;
      }
    }
    if ("calculation" in patch) {
      if (patch.calculation) column.calculation = cloneValue(patch.calculation);
      else delete column.calculation;
      delete column.expr;
      if (patch.calculation) {
        delete column.binding;
        delete column.key;
      }
    }
    if (patch.visible !== undefined) column.visible = patch.visible;
    const style = asRecord(column.style);
    for (const key of ["fontSize", "color", "backgroundColor"] as const) {
      if (patch[key] !== undefined) style[key] = patch[key];
    }
    column.style = cleanRecord(style);
  }, selection);
}

export function resizeNativeTableColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, width: number, adjacentColumnId?: string, adjacentWidth?: number): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.tableId);
    if (!table || table.type !== "table") return;
    const columns = nativeTableColumns(table);
    const column = columns.find((candidate) => candidate.id === selection.columnId);
    if (!column) return;
    column.width = Math.max(24, width);
    const adjacent = adjacentColumnId ? columns.find((candidate) => candidate.id === adjacentColumnId) : undefined;
    if (adjacent && adjacentWidth !== undefined) adjacent.width = Math.max(24, adjacentWidth);
    normalizeNativeTableColumnWidths(table, Number(table.width || 0));
  }, selection);
}

export function resizeNativeTable(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, width: number): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.elementIds[0]);
    if (!table || table.type !== "table") return;
    resizeNativeTableToWidth(draft, table, width);
  }, selection);
}

export function fitNativeTableToPrintableWidth(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.elementIds[0]);
    if (!table || table.type !== "table") return;
    const maxWidth = Math.max(NATIVE_TABLE_MIN_COLUMN_WIDTH, nativeBandContentWidth(draft) - Math.max(0, Number(table.x || 0)));
    resizeNativeTableToWidth(draft, table, maxWidth);
  }, selection);
}

export function reorderNativeTableColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, direction: -1 | 1): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.tableId);
    if (!table || table.type !== "table") return;
    const columns = nativeTableColumns(table);
    const index = columns.findIndex((column) => column.id === selection.columnId);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= columns.length) return;
    const [column] = columns.splice(index, 1);
    columns.splice(target, 0, column);
  }, selection);
}

export function moveNativeTableColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>, targetIndex: number): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.tableId);
    if (!table || table.type !== "table") return;
    const columns = nativeTableColumns(table);
    const index = columns.findIndex((column) => column.id === selection.columnId);
    if (index < 0) return;
    const nextIndex = Math.max(0, Math.min(columns.length - 1, Math.trunc(Number(targetIndex) || 0)));
    if (index === nextIndex) return;
    const [column] = columns.splice(index, 1);
    columns.splice(nextIndex, 0, column);
  }, selection);
}

export function addNativeTableSort(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, field: string): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    const sort = nativeTableSort(table);
    if (sort.some((descriptor) => String(descriptor.field || "") === field)) return;
    sort.push({ field, direction: "asc", type: "auto", nulls: "last" });
  });
}

export function setNativeTableSortDirection(document: DesignerDocument, selection: NativeTableSelection, field: string, direction: "asc" | "desc", type: "auto" | "date" | "number" | "string" = "auto"): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    const sort = nativeTableSort(table);
    const existingIndex = sort.findIndex((descriptor) => String(descriptor.field || "") === field);
    if (existingIndex >= 0) {
      sort[existingIndex].direction = direction;
      sort[existingIndex].type = String(sort[existingIndex].type || "auto") === "auto" ? type : sort[existingIndex].type;
      sort[existingIndex].nulls = sort[existingIndex].nulls || "last";
      for (let index = sort.length - 1; index > existingIndex; index -= 1) {
        if (String(sort[index].field || "") === field) sort.splice(index, 1);
      }
      return;
    }
    sort.push({ field, direction, type, nulls: "last" });
  });
}

export function updateNativeTableSort(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, patch: NativeTableSortPatch): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    const sort = nativeTableSort(table);
    const descriptor = sort[index];
    if (!descriptor) return;
    if (patch.field !== undefined) descriptor.field = patch.field;
    if (patch.direction !== undefined) descriptor.direction = patch.direction;
    if (patch.type !== undefined) descriptor.type = patch.type;
    if (patch.nulls !== undefined) descriptor.nulls = patch.nulls;
  });
}

export function removeNativeTableSort(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    const sort = nativeTableSort(table);
    if (index < 0 || index >= sort.length) return;
    sort.splice(index, 1);
    if (!sort.length) delete table.sort;
  });
}

export function moveNativeTableSort(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, index: number, direction: -1 | 1): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    const sort = nativeTableSort(table);
    const target = index + direction;
    if (index < 0 || target < 0 || target >= sort.length) return;
    const [descriptor] = sort.splice(index, 1);
    sort.splice(target, 0, descriptor);
  });
}

export function updateNativeTableGroup(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" | "nativeTableGroup" }>, patch: NativeTableGroupPatch): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    if (patch.enabled === false) {
      delete table.groups;
      return;
    }
    const groups = nativeTableGroups(table);
    let group = groups[0];
    const previousField = group ? String(group.field || asRecord(group.key).field || "") : "";
    const previousColumn = nativeTableColumns(table).find((column) => nativeColumnFieldKey(column) === previousField);
    const wasGroupedFieldVisible = previousColumn?.visible !== false;
    if (!group) {
      group = { id: uniqueNativeTableGroupId(table), field: patch.field || firstNativeTableBinding(table), sort: "asc", header: { visible: true, label: "Group" }, footer: { visible: false }, pagination: { repeatHeaderOnContinuation: true, keepHeaderWithFirstRow: true, keepFooterWithLastRow: true } };
      groups.push(group);
    }
    if (patch.field !== undefined) group.field = patch.field || undefined;
    if (patch.sort !== undefined) group.sort = patch.sort;
    if (patch.headerVisible !== undefined || patch.headerLabel !== undefined) {
      const header = asRecord(group.header);
      if (patch.headerVisible !== undefined) header.visible = patch.headerVisible;
      if (patch.headerLabel !== undefined) header.label = patch.headerLabel || undefined;
      group.header = cleanRecord(header);
    }
    if (patch.footerVisible !== undefined) {
      const footer = asRecord(group.footer);
      footer.visible = patch.footerVisible;
      if (patch.footerVisible && !footer.label && !asArray(footer.cells).length) footer.label = "Subtotal";
      group.footer = cleanRecord(footer);
    }
    if (patch.spacingAfter !== undefined) {
      const pagination = asRecord(group.pagination);
      pagination.spacingAfter = Math.max(0, Number(patch.spacingAfter) || 0);
      group.pagination = cleanRecord(pagination);
    }
    const paginationPatchKeys = ["pageBreakBefore", "repeatHeaderOnContinuation", "keepHeaderWithFirstRow", "keepFooterWithLastRow"] as const;
    if (paginationPatchKeys.some((key) => patch[key] !== undefined)) {
      const pagination = asRecord(group.pagination);
      for (const key of paginationPatchKeys) {
        if (patch[key] !== undefined) pagination[key] = patch[key];
      }
      group.pagination = cleanRecord(pagination);
    }
    const nextField = String(group.field || asRecord(group.key).field || "");
    if (patch.field !== undefined && previousField && previousField !== nextField && !wasGroupedFieldVisible && previousColumn) {
      previousColumn.visible = true;
    }
    if (patch.showGroupedFieldInDetail !== undefined || (patch.field !== undefined && !wasGroupedFieldVisible)) {
      const column = nativeTableColumns(table).find((candidate) => nativeColumnFieldKey(candidate) === nextField);
      if (column) column.visible = patch.showGroupedFieldInDetail !== undefined ? patch.showGroupedFieldInDetail : false;
    }
  }, selection.type === "nativeTableGroup" ? selection : { type: "nativeTableGroup", bandId: selection.bandId, tableId: selection.elementIds[0], region: "group" });
}

export function updateNativeTableSummary(document: DesignerDocument, selection: NativeTableSelection, patch: NativeTableSummaryPatch): DesignerCommandResult {
  return mutateNativeTable(document, selection, (table) => {
    const columns = nativeTableColumns(table);
    const visibleColumns = columns.filter((column) => column.visible !== false);
    const outputColumn = visibleColumns[visibleColumns.length - 1] || columns[columns.length - 1];
    const labelColumn = visibleColumns[0] || columns[0];
    const footer = summaryFooterForPatch(table, patch);
    if (!footer || !labelColumn || !outputColumn) return;
    if (patch.enabled !== undefined) {
      if (patch.target === "groupFooter") footer.visible = patch.enabled;
      else footer.enabled = patch.enabled;
    }
    if (patch.rowHeight !== undefined) footer.height = Math.max(0, Number(patch.rowHeight) || 0);
    const cells = asArray(footer.cells).map(asRecord);
    if (!cells.length) {
      cells.push(summaryLabelCell(labelColumn.id, Math.max(1, visibleColumns.length - 1), patch.target === "groupFooter" ? "Subtotal" : "Grand Total"));
      cells.push(summaryAggregateCell(outputColumn, patch.target === "groupFooter" ? "group" : "report"));
    }
    const labelCell = cells.find((cell) => !cell.aggregate) || cells[0];
    const aggregateCells = cells.filter((cell) => cell.aggregate);
    if (patch.label !== undefined) labelCell.value = patch.label;
    if (patch.labelColSpan !== undefined) labelCell.colSpan = nativeColSpanForVisibleSpan(columns, String(labelCell.columnId || labelColumn.id || ""), Math.max(1, Number(patch.labelColSpan) || 1));
    if (patch.fontSize !== undefined || patch.color !== undefined || patch.backgroundColor !== undefined || patch.bold !== undefined) {
      for (const cell of cells) {
        const style = asRecord(cell.style);
        if (patch.fontSize !== undefined) style.fontSize = patch.fontSize;
        if (patch.color !== undefined) style.color = patch.color || undefined;
        if (patch.backgroundColor !== undefined) style.backgroundColor = patch.backgroundColor || undefined;
        if (patch.bold !== undefined) style.bold = patch.bold;
        cell.style = cleanRecord(style);
      }
    }
    if (patch.addAggregate) {
      cells.push(summaryAggregateCell(outputColumn, patch.target === "groupFooter" ? "group" : "report"));
    }
    if (patch.removeAggregateIndex !== undefined) {
      const cell = aggregateCells[patch.removeAggregateIndex];
      const index = cells.indexOf(cell);
      if (index >= 0) cells.splice(index, 1);
    }
    if (patch.aggregate && patch.aggregateIndex !== undefined) {
      let cell = aggregateCells[patch.aggregateIndex];
      if (!cell) {
        cell = summaryAggregateCell(outputColumn, patch.target === "groupFooter" ? "group" : "report");
        cells.push(cell);
      }
      if (patch.aggregate.columnId !== undefined) cell.columnId = patch.aggregate.columnId;
      if (patch.aggregate.format !== undefined) cell.format = patch.aggregate.format || undefined;
      if (patch.aggregate.horizontalAlign !== undefined) cell.horizontalAlign = patch.aggregate.horizontalAlign;
      const aggregate = asRecord(cell.aggregate);
      if (patch.aggregate.fn !== undefined) aggregate.fn = patch.aggregate.fn;
      if (patch.aggregate.field !== undefined) aggregate.field = patch.aggregate.field;
      if (patch.aggregate.scope !== undefined) aggregate.scope = patch.aggregate.scope;
      cell.aggregate = cleanRecord(aggregate);
    }
    footer.cells = cells;
  }, selection.type === "nativeTableGroup" ? selection : { type: "nativeTableGroup", bandId: selection.bandId, tableId: selection.type === "nativeElement" ? selection.elementIds[0] : selection.tableId, region: patch.target === "tableFooter" ? "tableFooter" : "footer" });
}

export function duplicateNativeTableColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>): DesignerCommandResult {
  const source = findNativeTableColumn(document, selection.bandId, selection.tableId, selection.columnId);
  const nextColumnId = uniqueNativeTableColumnId(findNativeElement(document, selection.bandId, selection.tableId), `${source?.id || "column"}-copy`);
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.tableId);
    if (!table || table.type !== "table") return;
    const columns = nativeTableColumns(table);
    const index = columns.findIndex((column) => column.id === selection.columnId);
    if (index < 0) return;
    const copy = cloneValue(columns[index]);
    copy.id = nextColumnId;
    if (internalSessionOf(document)) reidentifyConditionalCopy(draft.renderer.layout, copy);
    columns.splice(index + 1, 0, copy);
  }, { ...selection, columnId: nextColumnId });
}

export function deleteNativeTableColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeTableColumn" }>): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const table = findNativeElement(draft, selection.bandId, selection.tableId);
    if (!table || table.type !== "table") return;
    table.columns = nativeTableColumns(table).filter((column) => column.id !== selection.columnId);
    const cells = asArray(asRecord(table.footer).cells).map(asRecord).filter((cell) => cell.columnId !== selection.columnId);
    table.footer = { ...asRecord(table.footer), cells };
  }, { type: "nativeElement", bandId: selection.bandId, elementIds: [selection.tableId] });
}

export function addNativeInvoiceHeaderContainer(document: DesignerDocument, bandId: string): DesignerCommandResult {
  const band = findNativeBand(document, bandId);
  const elementId = uniqueNativeElementId(band || {}, "invoice-header");
  return mutateDocument(document, (draft) => {
    const draftBand = findNativeBand(draft, bandId);
    if (!draftBand) return;
    nativeElements(draftBand).push({
      id: elementId,
      type: "container",
      x: 0,
      y: 0,
      width: 540,
      height: 156,
      padding: 10,
      gap: 14,
      layout: { mode: "columns", gap: 14, columns: [{ width: 88 }, { width: "1.2fr" }, { width: "1fr" }, { width: "1fr" }] },
      style: { borderColor: "#d8e1ec", borderWidth: 1, backgroundColor: "#ffffff" },
      elements: [
        {
          id: `${elementId}-logo`,
          type: "image",
          x: 0,
          y: 0,
          width: 88,
          height: 64,
          asset: "companyLogo",
          fit: "contain",
          lockAspectRatio: true,
          style: { borderColor: "#e7edf4", borderWidth: 1, backgroundColor: "#ffffff" },
        },
        invoiceHeaderColumn(`${elementId}-company`, [
          nativeText(`${elementId}-company-label`, "Company", 0),
          nativeField(`${elementId}-company-name`, "invoiceHeader.companyName", 16),
          nativeField(`${elementId}-company-address1`, "invoiceHeader.companyAddressLine1", 34),
          nativeField(`${elementId}-company-address2`, "invoiceHeader.companyAddressLine2", 50),
          nativeField(`${elementId}-company-contact`, "invoiceHeader.companyContact", 68),
        ]),
        invoiceHeaderColumn(`${elementId}-bill-to`, [
          nativeText(`${elementId}-bill-to-label`, "Bill To", 0),
          nativeField(`${elementId}-bill-to-value`, "invoiceHeader.clientName", 16),
          nativeField(`${elementId}-bill-to-address1`, "invoiceHeader.clientAddressLine1", 34),
          nativeField(`${elementId}-bill-to-address2`, "invoiceHeader.clientAddressLine2", 50),
          nativeText(`${elementId}-attention-label`, "Attention", 76),
          nativeField(`${elementId}-attention-value`, "invoiceHeader.attentionName", 92),
        ]),
        invoiceHeaderColumn(`${elementId}-invoice-meta`, [
          nativeText(`${elementId}-invoice-title`, "Invoice", 0),
          nativeText(`${elementId}-number-label`, "Invoice #", 28),
          nativeField(`${elementId}-number-value`, "invoiceHeader.invoiceNumber", 44),
          nativeText(`${elementId}-date-label`, "Invoice Date", 66),
          nativeField(`${elementId}-date-value`, "invoiceHeader.invoiceDate", 82, "date"),
          nativeText(`${elementId}-terms-label`, "Terms", 104),
          nativeField(`${elementId}-terms-value`, "invoiceHeader.dueTerms", 120),
        ]),
      ],
    });
    draftBand.height = Math.max(Number(draftBand.height || 48), 180);
  }, { type: "nativeElement", bandId, elementIds: [elementId] });
}

export function updateNativeElement(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, patch: NativeElementPatch): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, selection.bandId);
    if (!band) return;
    const selected = new Set(selection.elementIds);
    flattenNativeElements(band).forEach((element) => {
      if (!selected.has(String(element.id))) return;
      for (const key of ["text", "binding", "format", "asset", "fit"] as const) {
        if (patch[key] !== undefined) element[key] = patch[key] || undefined;
      }
      if (patch.lockAspectRatio !== undefined) element.lockAspectRatio = patch.lockAspectRatio;
      for (const key of ["x", "y", "width", "height"] as const) {
        if (patch[key] !== undefined) element[key] = Math.max(key === "width" || key === "height" ? 6 : 0, Number(patch[key]) || 0);
      }
      const style = asRecord(element.style);
      for (const key of ["fontSize", "bold", "italic", "color", "backgroundColor", "horizontalAlign", "verticalAlign", "lineHeight"] as const) {
        if (patch[key] !== undefined) style[key] = patch[key];
      }
      for (const key of ["borderColor", "borderWidth"] as const) {
        if (patch[key] !== undefined) style[key] = patch[key];
      }
      element.style = cleanRecord(style);
      if (patch.padding !== undefined) element.padding = Math.max(0, Number(patch.padding) || 0);
      if (patch.gap !== undefined) {
        element.gap = Math.max(0, Number(patch.gap) || 0);
        element.layout = { ...asRecord(element.layout), gap: element.gap };
      }
      if (patch.layoutMode !== undefined) {
        const layout = asRecord(element.layout);
        layout.mode = patch.layoutMode;
        if (patch.layoutMode === "columns" && !Array.isArray(layout.columns)) layout.columns = [{ width: "1fr" }, { width: "1fr" }, { width: "1fr" }];
        element.layout = cleanRecord(layout);
      }
    });
  }, selection);
}

export function moveNativeElements(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, delta: { dx: number; dy: number }): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, selection.bandId);
    if (!band) return;
    const selected = new Set(selection.elementIds);
    flattenNativeElements(band).forEach((element) => {
      if (!selected.has(String(element.id))) return;
      element.x = Math.max(0, Number(element.x || 0) + delta.dx);
      element.y = Math.max(0, Number(element.y || 0) + delta.dy);
    });
  }, selection);
}

export function resizeNativeElement(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, bounds: Partial<NativeElementBounds>): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const element = findNativeElement(draft, selection.bandId, selection.elementIds[0]);
    if (!element) return;
    const originalWidth = Math.max(6, Number(element.width || 6));
    const originalHeight = Math.max(6, Number(element.height || 6));
    let nextWidth = bounds.width !== undefined ? Math.max(6, bounds.width) : originalWidth;
    let nextHeight = bounds.height !== undefined ? Math.max(6, bounds.height) : originalHeight;
    if (element.type === "image" && element.lockAspectRatio !== false) {
      const ratio = originalWidth / originalHeight;
      const widthDelta = Math.abs(nextWidth - originalWidth) / originalWidth;
      const heightDelta = Math.abs(nextHeight - originalHeight) / originalHeight;
      if (bounds.width !== undefined && (bounds.height === undefined || widthDelta >= heightDelta)) nextHeight = nextWidth / ratio;
      else if (bounds.height !== undefined) nextWidth = nextHeight * ratio;
    }
    if (bounds.x !== undefined) element.x = Math.max(0, bounds.x);
    if (bounds.y !== undefined) element.y = Math.max(0, bounds.y);
    if (bounds.width !== undefined || (element.type === "image" && element.lockAspectRatio !== false && bounds.height !== undefined)) element.width = nextWidth;
    if (bounds.height !== undefined || (element.type === "image" && element.lockAspectRatio !== false && bounds.width !== undefined)) element.height = nextHeight;
  }, selection);
}

export function fitNativeBandToContent(document: DesignerDocument, bandId: string, height: number): DesignerCommandResult {
  return resizeNativeBand(document, bandId, height);
}

export function fitNativeContainerToContent(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, bounds: Partial<NativeElementBounds>): DesignerCommandResult {
  return resizeNativeElement(document, selection, bounds);
}

export function reparentNativeElement(
  document: DesignerDocument,
  selection: Extract<DesignerSelection, { type: "nativeElement" }>,
  targetParentId: string | null,
  nextBounds: NativeElementBounds,
): DesignerCommandResult {
  const elementId = selection.elementIds[0];
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, selection.bandId);
    if (!band || !elementId) return;
    const sourceSiblings = findNativeElementSiblings(band, elementId);
    if (!sourceSiblings) return;
    const sourceIndex = sourceSiblings.findIndex((element) => element.id === elementId);
    if (sourceIndex < 0) return;
    const [element] = sourceSiblings.splice(sourceIndex, 1);
    element.x = Math.max(0, nextBounds.x);
    element.y = Math.max(0, nextBounds.y);
    element.width = Math.max(6, nextBounds.width);
    element.height = Math.max(6, nextBounds.height);
    if (targetParentId) {
      const targetParent = findNativeElement(draft, selection.bandId, targetParentId);
      if (targetParent?.type === "container") {
        nativeElements(targetParent).push(element);
        return;
      }
    }
    nativeElements(band).push(element);
  }, { type: "nativeElement", bandId: selection.bandId, elementIds: [elementId] });
}

export function reorderNativeElement(document: DesignerDocument, bandId: string, elementId: string, direction: -1 | 1): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, bandId);
    if (!band) return;
    const siblings = findNativeElementSiblings(band, elementId);
    if (!siblings) return;
    const index = siblings.findIndex((element) => element.id === elementId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= siblings.length) return;
    const [element] = siblings.splice(index, 1);
    siblings.splice(targetIndex, 0, element);
  }, { type: "nativeElement", bandId, elementIds: [elementId] });
}

export function alignNativeElements(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "nativeElement" }>, alignment: NativeAlignment): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findNativeBand(draft, selection.bandId);
    if (!band) return;
    const elements = flattenNativeElements(band).filter((element) => selection.elementIds.includes(String(element.id)));
    if (elements.length < 2) return;
    const left = Math.min(...elements.map((element) => Number(element.x || 0)));
    const right = Math.max(...elements.map((element) => Number(element.x || 0) + Number(element.width || 0)));
    const top = Math.min(...elements.map((element) => Number(element.y || 0)));
    const bottom = Math.max(...elements.map((element) => Number(element.y || 0) + Number(element.height || 0)));
    if (alignment === "left") elements.forEach((element) => { element.x = left; });
    if (alignment === "right") elements.forEach((element) => { element.x = right - Number(element.width || 0); });
    if (alignment === "hCenter") elements.forEach((element) => { element.x = (left + right - Number(element.width || 0)) / 2; });
    if (alignment === "top") elements.forEach((element) => { element.y = top; });
    if (alignment === "bottom") elements.forEach((element) => { element.y = bottom - Number(element.height || 0); });
    if (alignment === "vCenter") elements.forEach((element) => { element.y = (top + bottom - Number(element.height || 0)) / 2; });
    if (alignment === "distributeH") distribute(elements, "x", "width");
    if (alignment === "distributeV") distribute(elements, "y", "height");
  }, selection);
}

export function updateDefinitionField(document: DesignerDocument, key: keyof Pick<DesignerDocument, "definitionId" | "name">, value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    (draft as unknown as JsonRecord)[key] = value;
  });
}

export function updateMetadataField(document: DesignerDocument, key: string, value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    draft.metadata = { ...draft.metadata, [key]: value };
  });
}

export function updateExecutionSources(document: DesignerDocument, data: DesignerDocument["data"]): DesignerCommandResult {
  return mutateDocument(document, draft => { draft.data = cloneValue(data); });
}

export function updatePageSetup(document: DesignerDocument, key: "size" | "orientation", value: DesignerPageSize | DesignerPageOrientation): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    draft.page = { ...draft.page, [key]: value };
    if (isNativeDocument(draft)) {
      const layout = asRecord(draft.renderer.layout);
      const page = asRecord(layout.page);
      page[key === "size" ? "size" : "orientation"] = key === "size" ? denormalizeNativePageSize(String(value)) : value;
      layout.page = page;
    }
  });
}

export function moveNativeBand(document: DesignerDocument, bandId: string, direction: -1 | 1): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const bands = nativeBands(draft);
    const index = bands.findIndex((band) => band.id === bandId);
    const targetIndex = index + direction;
    if (index < 0 || targetIndex < 0 || targetIndex >= bands.length) return;
    const [band] = bands.splice(index, 1);
    bands.splice(targetIndex, 0, band);
  }, { type: "band", bandId });
}

export function updateBand(document: DesignerDocument, bandId: string, key: "id" | "datasetId" | "styleId" | "outputs", value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const band = findBand(draft, bandId);
    const component = primaryComponent(band);
    if (!band || !component) return;
    if (key === "id") {
      const nextId = String(value || band.id);
      updateBandId(draft, band.id, nextId);
      return;
    }
    if (key === "outputs") {
      component.outputs = Array.isArray(value) ? value.map(String) : [];
      return;
    }
    if ("datasetId" in component && key === "datasetId") {
      component.datasetId = String(value || "");
    }
    if ("styleId" in component && key === "styleId") {
      component.styleId = value ? String(value) : undefined;
    }
  }, { type: "band", bandId: key === "id" ? String(value || bandId) : bandId });
}

export function updateComponent(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "component" }>, key: "datasetId" | "styleId" | "outputs", value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const component = findComponent(draft, selection.bandId, selection.componentId);
    if (!component) return;
    if (key === "outputs") {
      component.outputs = Array.isArray(value) ? value.map(String) : [];
      return;
    }
    if ("datasetId" in component && key === "datasetId") component.datasetId = String(value || "");
    if ("styleId" in component && key === "styleId") component.styleId = value ? String(value) : undefined;
  }, selection);
}

export function updateField(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "field" }>, key: keyof Pick<DesignerField, "label" | "key" | "format" | "horizontalAlign" | "styleId" | "column" | "row" | "marginTop">, value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const component = findComponent(draft, selection.bandId, selection.componentId);
    if (!component || component.kind !== "keyValueGrid") return;
    const field = component.fields.find((item) => item.id === selection.fieldId);
    if (!field) return;
    if (key === "column" || key === "row" || key === "marginTop") {
      field[key] = value === undefined || value === "" ? undefined : Number(value);
    } else {
      field[key] = value ? String(value) : undefined;
    }
  }, selection);
}

export function updateColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "column" }>, key: keyof Pick<DesignerColumn, "label" | "key" | "format" | "horizontalAlign" | "width">, value: unknown): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const component = findComponent(draft, selection.bandId, selection.componentId);
    if (!component || component.kind !== "table") return;
    const column = component.columns.find((item) => item.id === selection.columnId);
    if (!column) return;
    if (key === "width") {
      column.width = clampNumber(value === undefined || value === "" ? undefined : Number(value), 4, 80);
    } else {
      column[key] = value ? String(value) : undefined;
    }
  }, selection);
}

export function addBand(document: DesignerDocument, kind: "text" | "keyValueGrid" | "table"): DesignerCommandResult {
  const next = cloneDesignerDocument(document);
  const bandId = uniqueBandId(next, kind);
  const componentId = `${bandId}__${kind}`;
  if (kind === "text") {
    next.bands.push({
      id: bandId,
      legacyId: bandId,
      role: "custom",
      title: "Text",
      componentIds: [componentId],
      components: [{ id: componentId, kind, outputs: ["preview", "pdf"], content: "New text section", renderer: {} }],
      renderer: {},
    });
  }
  if (kind === "keyValueGrid") {
    next.bands.push({
      id: bandId,
      legacyId: bandId,
      role: "reportHeader",
      title: "Report Header",
      componentIds: [componentId],
      components: [{ id: componentId, kind, datasetId: "", styleId: undefined, outputs: ["preview", "pdf", "excel"], fields: [], renderer: {} }],
      renderer: {},
    });
  }
  if (kind === "table") {
    next.bands.push({
      id: bandId,
      legacyId: bandId,
      role: "details",
      title: "Details",
      componentIds: [componentId],
      components: [{ id: componentId, kind, datasetId: "", styleId: undefined, outputs: ["preview", "pdf", "excel"], columns: [], renderer: {} }],
      renderer: {},
    });
  }
  return { document: next, selection: { type: "band", bandId } };
}

export function addFieldToBand(document: DesignerDocument, bandId: string, datasetName: string, fieldKey: string): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const component = primaryComponent(findBand(draft, bandId));
    if (!component) return;
    if (component.kind === "keyValueGrid") {
      const fieldId = uniqueItemId(component.fields, `${component.id}__field`);
      component.datasetId = component.datasetId || datasetName;
      component.fields.push({
        id: fieldId,
        legacyId: fieldId,
        label: `${titleize(fieldKey)}:`,
        key: fieldKey,
        column: 1,
        renderer: {},
      });
    }
    if (component.kind === "table") {
      const columnId = uniqueItemId(component.columns, `${component.id}__column`);
      component.datasetId = component.datasetId || datasetName;
      component.columns.push({
        id: columnId,
        legacyId: columnId,
        label: titleize(fieldKey),
        key: fieldKey,
        width: 12,
        renderer: {},
      });
    }
  }, { type: "band", bandId });
}

export function moveField(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "field" }>, column: number, insertIndex?: number): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const component = findComponent(draft, selection.bandId, selection.componentId);
    if (!component || component.kind !== "keyValueGrid") return;
    const currentIndex = component.fields.findIndex((field) => field.id === selection.fieldId);
    if (currentIndex < 0) return;
    const [field] = component.fields.splice(currentIndex, 1);
    field.column = Math.max(1, Math.floor(column));
    const targetIndex = insertIndex === undefined ? component.fields.length : Math.max(0, Math.min(insertIndex, component.fields.length));
    component.fields.splice(targetIndex, 0, field);
  }, selection);
}

export function reorderColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "column" }>, targetColumnId: string): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const component = findComponent(draft, selection.bandId, selection.componentId);
    if (!component || component.kind !== "table" || selection.columnId === targetColumnId) return;
    const from = component.columns.findIndex((column) => column.id === selection.columnId);
    const to = component.columns.findIndex((column) => column.id === targetColumnId);
    if (from < 0 || to < 0) return;
    const [column] = component.columns.splice(from, 1);
    component.columns.splice(to, 0, column);
  }, selection);
}

export function resizeColumn(document: DesignerDocument, selection: Extract<DesignerSelection, { type: "column" }>, width: number): DesignerCommandResult {
  return updateColumn(document, selection, "width", width);
}

export function moveBand(document: DesignerDocument, bandId: string, direction: -1 | 1): DesignerCommandResult {
  const next = cloneDesignerDocument(document);
  const index = next.bands.findIndex((band) => band.id === bandId);
  const targetIndex = index + direction;
  if (index < 0 || targetIndex < 0 || targetIndex >= next.bands.length) return { document };
  const [band] = next.bands.splice(index, 1);
  next.bands.splice(targetIndex, 0, band);
  return { document: next, selection: { type: "band", bandId } };
}

export function duplicateSelection(document: DesignerDocument, selection: DesignerSelection): DesignerCommandResult {
  if (internalSessionOf(document)) assertDesignerCommandAdmission(document);
  else assertReportAdmission(document, "copy");
  if (selection.type === "nativeTableColumn") {
    return duplicateNativeTableColumn(document, selection);
  }
  if (selection.type === "nativeElement") {
    const sourceBand = findNativeBand(document, selection.bandId);
    const sourceElements = sourceBand ? flattenNativeElements(sourceBand) : [];
    const selectedSourceElements = sourceElements.filter((element) => selection.elementIds.includes(String(element.id)));
    const nextIds = selectedSourceElements.map((element, index) => uniqueNativeElementId(sourceBand || {}, `${String(element.id || "element")}-copy-${index + 1}`));
    return mutateDocument(document, (draft) => {
      const band = findNativeBand(draft, selection.bandId);
      if (!band) return;
      const elements = nativeElements(band);
      const selected = flattenNativeElements(band).filter((element) => selection.elementIds.includes(String(element.id)));
      selected.forEach((element, index) => {
        const copy = cloneNativeElement(element);
        copy.id = nextIds[index] || uniqueNativeElementId(band, String(element.id || "element"));
        if (internalSessionOf(document)) reidentifyConditionalCopy(draft.renderer.layout, copy, true);
        copy.x = Number(copy.x || 0) + 12;
        copy.y = Number(copy.y || 0) + 12;
        elements.push(copy);
      });
    }, { type: "nativeElement", bandId: selection.bandId, elementIds: nextIds.length ? nextIds : selection.elementIds });
  }
  if (selection.type === "band") {
    if (isNativeDocument(document)) {
      const sourceBands = nativeBands(document);
      const bandIndex = sourceBands.findIndex((band) => band.id === selection.bandId);
      if (bandIndex < 0) return { document };
      const copyId = uniqueNativeBandId(sourceBands, String(sourceBands[bandIndex].role || "band"));
      return mutateDocument(document, (draft) => {
        const bands = nativeBands(draft);
        const source = bands.find((band) => band.id === selection.bandId);
        if (!source) return;
        const copy = cloneValue(source);
        copy.id = copyId;
        if (internalSessionOf(document)) reidentifyConditionalCopy(draft.renderer.layout, copy, true);
        bands.splice(bandIndex + 1, 0, copy);
      }, { type: "band", bandId: copyId });
    }
    const next = cloneDesignerDocument(document);
    const bandIndex = next.bands.findIndex((band) => band.id === selection.bandId);
    if (bandIndex < 0) return { document };
    const copy = cloneDesignerDocument({ ...next, bands: [next.bands[bandIndex]] }).bands[0];
    copy.id = uniqueBandId(next, copy.id);
    copy.legacyId = copy.id;
    copy.componentIds = copy.componentIds.map((componentId) => `${copy.id}__${componentId.split("__").pop() || "component"}`);
    copy.components = copy.components.map((component, index) => reidentifyComponent(component, copy.componentIds[index] || `${copy.id}__component-${index + 1}`));
    next.bands.splice(bandIndex + 1, 0, copy);
    return { document: next, selection: { type: "band", bandId: copy.id } };
  }
  if (selection.type === "field") {
    return mutateDocument(document, (draft) => {
      const component = findComponent(draft, selection.bandId, selection.componentId);
      if (!component || component.kind !== "keyValueGrid") return;
      const index = component.fields.findIndex((field) => field.id === selection.fieldId);
      if (index < 0) return;
      const copy = { ...component.fields[index], renderer: { ...component.fields[index].renderer } };
      copy.id = uniqueItemId(component.fields, `${component.id}__field`);
      copy.legacyId = copy.id;
      component.fields.splice(index + 1, 0, copy);
    }, selection);
  }
  if (selection.type === "column") {
    return mutateDocument(document, (draft) => {
      const component = findComponent(draft, selection.bandId, selection.componentId);
      if (!component || component.kind !== "table") return;
      const index = component.columns.findIndex((column) => column.id === selection.columnId);
      if (index < 0) return;
      const copy = { ...component.columns[index], renderer: { ...component.columns[index].renderer } };
      copy.id = uniqueItemId(component.columns, `${component.id}__column`);
      copy.legacyId = copy.id;
      component.columns.splice(index + 1, 0, copy);
    }, selection);
  }
  return { document };
}

export function deleteSelection(document: DesignerDocument, selection: DesignerSelection): DesignerCommandResult {
  if (selection.type === "nativeTableColumn") {
    return deleteNativeTableColumn(document, selection);
  }
  if (selection.type === "nativeElement") {
    return mutateDocument(document, (draft) => {
      const band = findNativeBand(draft, selection.bandId);
      if (!band) return;
      const selected = new Set(selection.elementIds);
      removeNativeElements(band, selected);
    }, { type: "band", bandId: selection.bandId });
  }
  if (selection.type === "band") {
    if (isNativeDocument(document)) {
      return mutateDocument(document, (draft) => {
        setNativeBands(draft, nativeBands(draft).filter((band) => band.id !== selection.bandId));
      }, { type: "report" });
    }
    const next = cloneDesignerDocument(document);
    next.bands = next.bands.filter((band) => band.id !== selection.bandId);
    return { document: next, selection: { type: "report" } };
  }
  if (selection.type === "component") {
    return { document, selection: { type: "band", bandId: selection.bandId } };
  }
  if (selection.type === "field") {
    return mutateDocument(document, (draft) => {
      const component = findComponent(draft, selection.bandId, selection.componentId);
      if (component?.kind === "keyValueGrid") {
        component.fields = component.fields.filter((field) => field.id !== selection.fieldId);
      }

    }, { type: "band", bandId: selection.bandId });
  }
  if (selection.type === "column") {
    return mutateDocument(document, (draft) => {
      const component = findComponent(draft, selection.bandId, selection.componentId);
      if (component?.kind === "table") {
        component.columns = component.columns.filter((column) => column.id !== selection.columnId);
      }
    }, { type: "band", bandId: selection.bandId });
  }
  return { document };
}

export function applyConditionalRuleCommand(document: DesignerDocument, selection: DesignerSelection, targetKey: string, action: ConditionalRuleAction): DesignerCommandResult {
  return mutateDocument(document, draft => mutateConditionalRule(draft, selection, targetKey, action), selection);
}

export function setInternalValueModeCommand(document: DesignerDocument, selection: DesignerSelection, binding?: string): DesignerCommandResult {
  if (!isInternalFormulaSession(internalSessionOf(document)) || !formulaTextTarget(document, selection)) throw new Error("Value mode requires an internal direct presentation element.");
  if (binding !== undefined) {
    const context = formulaEditingContext(document, selection);
    const fields = formulaFields(context.payload);
    if (!fields.some(field => field.path === binding)) throw new Error("The selected binding is unavailable in this presentation scope.");
  }
  return mutateDocument(document, draft => {
    const target = formulaTextTarget(draft, selection)!;
    delete target.expr; delete target.binding; delete target.text;
    target.type = binding === undefined ? "text" : "field";
    if (binding === undefined) target.text = "";
    else target.binding = binding;
  }, selection);
}

export function applyInternalFormulaCommand(document: DesignerDocument, selection: DesignerSelection, node: unknown, remove = false): DesignerCommandResult {
  const session = internalSessionOf(document);
  if (!isInternalFormulaSession(session) || (!isFormulaTarget(selection) && !formulaTextTarget(document, selection))) throw new Error("Apply is available only for controlled formula workspace targets.");
  const context = formulaEditingContext(document, selection);
  const analysis = analyzeFormula(context.payload, context.targetId, node, 0, remove, false);
  if (!analysis.valid) throw new Error(analysis.error);
  return mutateDocument(document, draft => {
    const column = isFormulaTarget(selection) ? formulaColumn(draft, selection.columnId) : formulaTextTarget(draft, selection)!;
    delete column.expr;
    delete column.calculation;
    delete column.binding;
    delete column.key;
    if (!isFormulaTarget(selection)) column.type = "text";
    if (remove) {
      if (isFormulaTarget(selection)) {
        const original = formulaColumn(session.initialPayload, selection.columnId);
        if (original.expr !== undefined) column.expr = cloneValue(original.expr);
        else if (original.binding !== undefined) column.binding = original.binding;
        else throw new Error("This calculated field has no original source binding. Replace its formula or explicitly delete the field.");
      } else column.text = String(formulaTextTarget(session.initialPayload, selection)?.text || "");
    }
    else {
      delete column.text;
      column.expr = analysis.canonical;
      const layout = draft.renderer.layout;
      layout.requiredFeatures = [...new Set([...asArray(layout.requiredFeatures), TYPED_EXPRESSIONS_CAPABILITY])];
      if (inspectTypedExpressionStructure(analysis.canonical).hasParameters) layout.requiredFeatures = [...new Set([...asArray(layout.requiredFeatures), REPORT_PARAMETERS_CAPABILITY])];
    }
  }, selection);
}

export function applyInternalParametersCommand(document: DesignerDocument, parameters: readonly ReportParameter[]): DesignerCommandResult {
  if (!isInternalFormulaSession(internalSessionOf(document))) throw new Error("Parameter authoring requires the controlled internal workspace.");
  const nextParameters = compileReportParameters(parameters);
  for (const previous of compileReportParameters(document.renderer.layout.parameters)) {
    const next = nextParameters.find(parameter => parameter.id === previous.id);
    const dependencies = parameterDependencies(document.renderer.layout, previous.id);
    if (dependencies.length && (!next || next.type !== previous.type || next.nullable !== previous.nullable)) throw new Error(`Parameter "${previous.label}" is referenced by ${dependencies.join(", ")}. Repair those formulas/filters before deleting it or changing its type/nullability.`);
  }
  return mutateDocument(document, draft => {
    draft.renderer.layout.parameters = cloneValue(nextParameters);
    draft.renderer.layout.requiredFeatures = [...new Set([...asArray(draft.renderer.layout.requiredFeatures), REPORT_PARAMETERS_CAPABILITY])];
  });
}

export function applyInternalFiltersCommand(document: DesignerDocument, filters: readonly PresentationFilter[]): DesignerCommandResult {
  if (!isInternalFormulaSession(internalSessionOf(document))) throw new Error("Filter authoring requires the controlled internal workspace.");
  return mutateDocument(document, draft => {
    draft.renderer.layout.presentationFilters = cloneValue(filters);
    draft.renderer.layout.requiredFeatures = [...new Set([...asArray(draft.renderer.layout.requiredFeatures), REPORT_PARAMETERS_CAPABILITY, TYPED_EXPRESSIONS_CAPABILITY])];
  });
}

function mutateDocument(document: DesignerDocument, updater: (draft: DesignerDocument) => void, selection?: DesignerSelection): DesignerCommandResult {
  const next = cloneDesignerDocument(document);
  updater(next);
  assertDesignerCommandAdmission(next);
  return { document: next, selection };
}

function cloneDesignerDocument(document: DesignerDocument): DesignerDocument {
  assertDesignerCommandAdmission(document);
  const next = cloneDocumentSnapshot(document);
  inheritInternalSession(document, next);
  return next;
}

function clampNumber(value: unknown, min: number, max: number): number | undefined {
  const number = Number(value);
  if (!Number.isFinite(number)) return undefined;
  return Math.min(max, Math.max(min, number));
}

function denormalizeNativePageSize(value: string): string {
  if (value.toLowerCase() === "legal") return "Legal";
  if (value.toLowerCase() === "a4") return "A4";
  return "Letter";
}

function updateBandId(document: DesignerDocument, currentId: string, nextId: string) {
  const band = findBand(document, currentId);
  if (!band || currentId === nextId) return;
  band.id = nextId;
  band.legacyId = nextId;
}

function uniqueBandId(document: DesignerDocument, prefix: string): string {
  const used = new Set(document.bands.map((band) => band.id));
  let index = document.bands.length + 1;
  let candidate = `${prefix}-${index}`;
  while (used.has(candidate)) {
    index += 1;
    candidate = `${prefix}-${index}`;
  }
  return candidate;
}

function uniqueItemId(items: Array<{ id: string }>, prefix: string): string {
  const used = new Set(items.map((item) => item.id));
  let index = items.length + 1;
  let candidate = `${prefix}-${index}`;
  while (used.has(candidate)) {
    index += 1;
    candidate = `${prefix}-${index}`;
  }
  return candidate;
}

function reidentifyComponent(component: DesignerComponent, componentId: string): DesignerComponent {
  const base = {
    ...component,
    id: componentId,
    renderer: cleanRecord(asRecord(component.renderer)),
  };
  if (component.kind === "keyValueGrid") {
    return {
      ...base,
      kind: "keyValueGrid",
      fields: component.fields.map((field, index) => ({ ...field, id: `${componentId}__field-${index + 1}`, legacyId: `${componentId}__field-${index + 1}` })),
    };
  }
  if (component.kind === "table") {
    return {
      ...base,
      kind: "table",
      columns: component.columns.map((column, index) => ({ ...column, id: `${componentId}__column-${index + 1}`, legacyId: `${componentId}__column-${index + 1}` })),
    };
  }
  return base as DesignerComponent;
}

function isNativeDocument(document: DesignerDocument): boolean {
  return document.sourceSchemaVersion === "reports-v3.1" || Array.isArray(asRecord(document.renderer.layout).bands);
}

function nativeBands(document: DesignerDocument): JsonRecord[] {
  const layout = asRecord(document.renderer.layout);
  const bands = Array.isArray(layout.bands) ? layout.bands : [];
  if (!Array.isArray(layout.bands)) layout.bands = bands;
  bands.forEach((band, index) => {
    if (!band || typeof band !== "object" || Array.isArray(band)) bands[index] = {};
  });
  return bands as JsonRecord[];
}

function setNativeBands(document: DesignerDocument, bands: JsonRecord[]) {
  document.renderer.layout = { ...document.renderer.layout, bands };
}

function findNativeBand(document: DesignerDocument, bandId: string): JsonRecord | undefined {
  return nativeBands(document).find((band) => band.id === bandId);
}

function nativeElements(band: JsonRecord): JsonRecord[] {
  const elements = Array.isArray(band.elements) ? band.elements : [];
  if (!Array.isArray(band.elements)) band.elements = elements;
  elements.forEach((element, index) => {
    if (!element || typeof element !== "object" || Array.isArray(element)) elements[index] = {};
  });
  return elements as JsonRecord[];
}

function findNativeElement(document: DesignerDocument, bandId: string, elementId: string): JsonRecord | undefined {
  const band = findNativeBand(document, bandId);
  return band ? flattenNativeElements(band).find((element) => element.id === elementId) : undefined;
}

function uniqueNativeBandId(bands: JsonRecord[], role: string): string {
  const used = new Set(bands.map((band) => String(band.id || "")));
  const prefix = role.replace(/([a-z])([A-Z])/g, "$1-$2").toLowerCase();
  let index = bands.length + 1;
  let candidate = prefix;
  while (used.has(candidate)) {
    candidate = `${prefix}-${index}`;
    index += 1;
  }
  return candidate;
}

function uniqueNativeElementId(band: JsonRecord, prefix: string): string {
  const safePrefix = (prefix || "element").replace(/[^a-z0-9_-]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "element";
  const used = new Set(flattenNativeElements(band).map((element) => String(element.id || "")));
  let index = nativeElements(band).length + 1;
  let candidate = safePrefix;
  while (used.has(candidate)) {
    candidate = `${safePrefix}-${index}`;
    index += 1;
  }
  return candidate;
}

function flattenNativeElements(root: JsonRecord): JsonRecord[] {
  const result: JsonRecord[] = [];
  const visit = (elements: JsonRecord[]) => {
    elements.forEach((element) => {
      result.push(element);
      if (element.type === "container") visit(nativeElements(element));
    });
  };
  visit(nativeElements(root));
  return result;
}

function findNativeElementSiblings(root: JsonRecord, elementId: string): JsonRecord[] | null {
  const top = nativeElements(root);
  if (top.some((element) => element.id === elementId)) return top;
  for (const element of top) {
    if (element.type !== "container") continue;
    const found = findNativeElementSiblings(element, elementId);
    if (found) return found;
  }
  return null;
}

function removeNativeElements(root: JsonRecord, selected: Set<string>) {
  const next = nativeElements(root).filter((element) => !selected.has(String(element.id)));
  next.forEach((element) => {
    if (element.type === "container") removeNativeElements(element, selected);
  });
  root.elements = next;
}

function resolveNativeInsertionPlacement(
  document: DesignerDocument,
  band: JsonRecord,
  parent: JsonRecord | undefined,
  requestedAt: { x: number; y: number } | undefined,
  size: { width: number; height: number },
): NativeElementBounds {
  const parentLayout = asRecord(parent?.layout);
  const parentIsStructured = parent && parent.type === "container" && String(parentLayout.mode || "absolute") !== "absolute";
  const contentWidth = parent ? nativeContainerContentWidth(parent) : nativeBandContentWidth(document);
  const width = Math.max(6, Math.min(size.width, Math.max(6, contentWidth - NATIVE_INSERT_PADDING * 2)));
  const height = size.height;
  if (requestedAt) {
    return {
      x: clampNativePlacement(requestedAt.x, width, contentWidth),
      y: Math.max(0, requestedAt.y),
      width,
      height,
    };
  }
  if (parentIsStructured) {
    return { x: 0, y: 0, width, height };
  }
  const siblings = parent ? nativeElements(parent) : nativeElements(band);
  const x = Math.min(NATIVE_INSERT_PADDING, Math.max(0, contentWidth - width));
  const bottom = siblings.reduce((max, element) => Math.max(max, Number(element.y || 0) + Number(element.height || 0)), 0);
  const y = siblings.length ? bottom + NATIVE_INSERT_GAP : NATIVE_INSERT_PADDING;
  return { x, y, width, height };
}

function expandNativeBandForInsertion(
  document: DesignerDocument,
  band: JsonRecord,
  parent: JsonRecord | undefined,
  placement: NativeElementBounds,
) {
  if (parent && parent.type === "container" && String(asRecord(parent.layout).mode || "absolute") === "absolute") {
    const padding = nativeContainerPadding(parent);
    const requiredContainerHeight = Math.ceil(padding.top + placement.y + placement.height + padding.bottom + NATIVE_INSERT_PADDING);
    parent.height = Math.max(Number(parent.height || 0), requiredContainerHeight);
  }
  const bottomInBand = parent ? Number(parent.y || 0) + Number(parent.height || 0) : placement.y + placement.height;
  const requiredHeight = Math.ceil(bottomInBand + NATIVE_INSERT_PADDING);
  if (requiredHeight <= Number(band.height || 0)) return;
  band.height = Math.min(maxNativeBandHeight(document), requiredHeight);
}

function nativeBandContentWidth(document: DesignerDocument): number {
  const layout = asRecord(document.renderer.layout);
  const page = asRecord(layout.page);
  const sizeName = String(page.size || "Letter");
  const pageSize = NATIVE_PAGE_SIZES[sizeName] || NATIVE_PAGE_SIZES.Letter;
  const margins = asRecord(page.margins);
  const left = Number(margins.left ?? 36) || 36;
  const right = Number(margins.right ?? 36) || 36;
  return Math.max(0, pageSize.width - left - right);
}

function maxNativeBandHeight(document: DesignerDocument): number {
  const layout = asRecord(document.renderer.layout);
  const page = asRecord(layout.page);
  const sizeName = String(page.size || "Letter");
  const pageSize = NATIVE_PAGE_SIZES[sizeName] || NATIVE_PAGE_SIZES.Letter;
  const margins = asRecord(page.margins);
  const top = Number(margins.top ?? 36) || 36;
  const bottom = Number(margins.bottom ?? 36) || 36;
  return Math.max(12, pageSize.height - top - bottom);
}

function nativeContainerContentWidth(container: JsonRecord): number {
  const padding = nativeContainerPadding(container);
  return Math.max(0, Number(container.width || 0) - padding.left - padding.right);
}

function nativeContainerPadding(container: JsonRecord): { top: number; right: number; bottom: number; left: number } {
  const padding = container.padding;
  if (typeof padding === "number") return { top: padding, right: padding, bottom: padding, left: padding };
  const record = asRecord(padding);
  const fallback = Number(record.all || 0) || 0;
  return {
    top: Number(record.top ?? fallback) || 0,
    right: Number(record.right ?? fallback) || 0,
    bottom: Number(record.bottom ?? fallback) || 0,
    left: Number(record.left ?? fallback) || 0,
  };
}

function clampNativePlacement(x: number, width: number, contentWidth: number): number {
  return Math.max(0, Math.min(Number(x) || 0, Math.max(0, contentWidth - width)));
}

function cloneNativeElement(element: JsonRecord): JsonRecord {
  return JSON.parse(JSON.stringify(element)) as JsonRecord;
}

function nativeTableColumn(id: string, binding: string): JsonRecord {
  return {
    id: sanitizeId(id || binding || "column"),
    label: titleizeBinding(binding),
    binding,
    width: binding.toLowerCase().includes("description") ? "1fr" : 90,
    format: inferNativeFormat(binding),
    horizontalAlign: inferNativeFormat(binding) === "money" || inferNativeFormat(binding) === "number" ? "right" : "left",
  };
}

function resizeNativeTableToWidth(document: DesignerDocument, table: JsonRecord, requestedWidth: number): void {
  const maxWidth = Math.max(NATIVE_TABLE_MIN_COLUMN_WIDTH, nativeBandContentWidth(document) - Math.max(0, Number(table.x || 0)));
  const width = clampNumber(requestedWidth, NATIVE_TABLE_MIN_COLUMN_WIDTH, maxWidth) ?? NATIVE_TABLE_MIN_COLUMN_WIDTH;
  const previousWidth = Number(table.width || 0);
  table.width = width;
  scaleNativeTableColumnWidths(table, width, previousWidth);
}

function scaleNativeTableColumnWidths(table: JsonRecord, nextWidth: number, previousWidth?: number): void {
  const columns = nativeTableColumns(table);
  if (!columns.length) return;
  const currentTotal = columnWidthTotal(columns);
  const sourceTotal = currentTotal > NATIVE_TABLE_WIDTH_TOLERANCE ? currentTotal : (previousWidth && previousWidth > NATIVE_TABLE_WIDTH_TOLERANCE ? previousWidth : columns.length * NATIVE_TABLE_MIN_COLUMN_WIDTH);
  const scaled = columns.map((column) => Math.max(NATIVE_TABLE_MIN_COLUMN_WIDTH, numericColumnWidth(column, sourceTotal / columns.length) * nextWidth / sourceTotal));
  applyColumnWidths(columns, fitWidthsToTotal(scaled, nextWidth));
}

function normalizeNativeTableColumnWidths(table: JsonRecord, tableWidth: number): void {
  const columns = nativeTableColumns(table);
  if (!columns.length || tableWidth <= 0) return;
  const widths = columns.map((column) => Math.max(NATIVE_TABLE_MIN_COLUMN_WIDTH, numericColumnWidth(column, tableWidth / columns.length)));
  applyColumnWidths(columns, fitWidthsToTotal(widths, tableWidth));
}

function fitWidthsToTotal(widths: number[], total: number): number[] {
  if (!widths.length) return widths;
  const minTotal = widths.length * NATIVE_TABLE_MIN_COLUMN_WIDTH;
  const target = Math.max(minTotal, total);
  const current = widths.reduce((sum, width) => sum + width, 0);
  const adjustable = widths.map((width) => Math.max(0, width - NATIVE_TABLE_MIN_COLUMN_WIDTH));
  const adjustableTotal = adjustable.reduce((sum, width) => sum + width, 0);
  if (adjustableTotal <= NATIVE_TABLE_WIDTH_TOLERANCE) return widths.map(() => target / widths.length);
  const delta = target - current;
  return widths.map((width, index) => Math.max(NATIVE_TABLE_MIN_COLUMN_WIDTH, width + delta * adjustable[index] / adjustableTotal));
}

function applyColumnWidths(columns: JsonRecord[], widths: number[]): void {
  columns.forEach((column, index) => {
    column.width = Math.round((widths[index] ?? NATIVE_TABLE_MIN_COLUMN_WIDTH) * 100) / 100;
  });
}

function columnWidthTotal(columns: JsonRecord[]): number {
  return columns.reduce((sum, column) => sum + numericColumnWidth(column, 0), 0);
}

function numericColumnWidth(column: JsonRecord, fallback: number): number {
  const width = column.width;
  if (typeof width === "number" && Number.isFinite(width)) return Math.max(0, width);
  const parsed = Number.parseFloat(String(width || ""));
  return Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
}

function nativeTableColumns(table: JsonRecord): JsonRecord[] {
  const columns = Array.isArray(table.columns) ? table.columns : [];
  if (!Array.isArray(table.columns)) table.columns = columns;
  return columns as JsonRecord[];
}

function nativeTableSort(table: JsonRecord): JsonRecord[] {
  const sort = Array.isArray(table.sort) ? table.sort : [];
  if (!Array.isArray(table.sort)) table.sort = sort;
  return sort as JsonRecord[];
}

function nativeTableGroups(table: JsonRecord): JsonRecord[] {
  const groups = Array.isArray(table.groups) ? table.groups : [];
  if (!Array.isArray(table.groups)) table.groups = groups;
  return groups as JsonRecord[];
}

function mutateNativeTable(
  document: DesignerDocument,
  selection: NativeTableSelection,
  mutator: (table: JsonRecord) => void,
  nextSelection: DesignerSelection = selection,
): DesignerCommandResult {
  return mutateDocument(document, (draft) => {
    const tableId = selection.type === "nativeElement" ? selection.elementIds[0] : selection.tableId;
    const table = findNativeElement(draft, selection.bandId, tableId);
    if (!table || table.type !== "table") return;
    mutator(table);
  }, nextSelection);
}

function firstNativeTableBinding(table: JsonRecord): string {
  const column = nativeTableColumns(table).find((item) => typeof item.binding === "string" && item.binding);
  return String(column?.binding || column?.id || "");
}

function nativeColumnFieldKey(column: JsonRecord): string {
  return String(column.binding || column.key || column.id || "");
}

function uniqueNativeTableGroupId(table: JsonRecord): string {
  const existing = new Set(nativeTableGroups(table).map((group) => String(group.id || "")));
  let index = 1;
  let id = "group";
  while (existing.has(id)) {
    index += 1;
    id = `group-${index}`;
  }
  return id;
}

function findNativeTableColumn(document: DesignerDocument, bandId: string, tableId: string, columnId: string): JsonRecord | undefined {
  const table = findNativeElement(document, bandId, tableId);
  if (!table || table.type !== "table") return undefined;
  return nativeTableColumns(table).find((column) => column.id === columnId);
}

function uniqueNativeTableColumnId(table: JsonRecord | undefined, seed: string): string {
  const columns = table ? nativeTableColumns(table) : [];
  const used = new Set(columns.map((column) => String(column.id)));
  const base = sanitizeId(seed || "column") || "column";
  let candidate = base;
  let index = 2;
  while (used.has(candidate)) {
    candidate = `${base}-${index}`;
    index += 1;
  }
  return candidate;
}

function ensureNativeTableFooter(table: JsonRecord): JsonRecord {
  const columns = nativeTableColumns(table);
  const last = columns[columns.length - 1];
  const first = columns[0];
  return {
    enabled: true,
    cells: columns.map((column, index) => {
      if (index === 0) return { columnId: first?.id || column.id, value: "Total" };
      if (column.id === last?.id) return { columnId: column.id, aggregate: "sum", format: column.format || inferNativeFormat(String(column.binding || column.key || column.id)) };
      return { columnId: column.id, value: "" };
    }),
  };
}

function summaryFooterForPatch(table: JsonRecord, patch: NativeTableSummaryPatch): JsonRecord | null {
  if (patch.target === "tableFooter") {
    const footer = asRecord(table.footer);
    if (!Object.keys(footer).length) table.footer = { enabled: true, cells: [] };
    else table.footer = footer;
    return asRecord(table.footer);
  }
  const groups = nativeTableGroups(table);
  let group = groups[0];
  if (!group) {
    group = { id: uniqueNativeTableGroupId(table), field: firstNativeTableBinding(table), sort: "asc", header: { visible: true, label: "Group" }, footer: { visible: true, cells: [] } };
    groups.push(group);
  }
  const footer = asRecord(group.footer);
  group.footer = footer;
  return footer;
}

function summaryLabelCell(columnId: unknown, colSpan: number, value: string): JsonRecord {
  return { columnId: String(columnId || ""), colSpan, value, style: { bold: true, horizontalAlign: "right", backgroundColor: "#f8fbff" } };
}

function summaryAggregateCell(column: JsonRecord, scope: "group" | "report"): JsonRecord {
  const field = String(column.binding || column.key || column.id || "");
  return {
    columnId: String(column.id || field),
    aggregate: { fn: "sum", field, scope },
    format: String(column.format || "money"),
    horizontalAlign: "right",
    style: { bold: true, horizontalAlign: "right", backgroundColor: scope === "report" ? "#eef4ff" : "#f8fbff" },
  };
}

function nativeColSpanForVisibleSpan(columns: JsonRecord[], startColumnId: string, visibleSpan: number): number {
  const startIndex = Math.max(0, columns.findIndex((column) => String(column.id || "") === startColumnId));
  let consumedVisible = 0;
  let span = 0;
  for (let index = startIndex; index < columns.length && consumedVisible < visibleSpan; index += 1) {
    span += 1;
    const column = columns[index];
    if (column.visible !== false && column.hidden !== true) consumedVisible += 1;
  }
  return Math.max(1, span);
}

function normalizeNativeTableFooter(table: JsonRecord): JsonRecord {
  const footer = ensureNativeTableFooter(table);
  const existing = new Map(asArray(asRecord(table.footer).cells).map(asRecord).map((cell) => [String(cell.columnId), cell]));
  const footerCells = asArray(footer.cells).map(asRecord);
  return {
    ...asRecord(table.footer),
    cells: footerCells.map((cell) => ({ ...cell, ...asRecord(existing.get(String(cell.columnId))) })),
  };
}

function sanitizeId(value: string): string {
  const normalized = String(value || "column").trim().replace(/([a-z0-9])([A-Z])/g, "$1-$2").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");
  return normalized || "column";
}

function titleizeBinding(value: string): string {
  return String(value || "Column")
    .split(".").pop()!
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function inferNativeFormat(binding: string): string | undefined {
  const text = binding.toLowerCase();
  if (text.includes("amount") || text.includes("rate") || text.includes("total") || text.includes("cost") || text.includes("price")) return "money";
  if (text.includes("date")) return "date";
  if (text.includes("qty") || text.includes("quantity") || text.includes("hours") || text.includes("days") || text.includes("count")) return "number";
  return undefined;
}

function invoiceHeaderColumn(id: string, elements: JsonRecord[]): JsonRecord {
  return {
    id,
    type: "container",
    x: 0,
    y: 0,
    width: 160,
    height: 112,
    padding: 0,
    gap: 4,
    layout: { mode: "vertical", gap: 4 },
    style: { backgroundColor: "transparent" },
    elements,
  };
}

function nativeText(id: string, text: string, y: number): JsonRecord {
  return {
    id,
    type: "text",
    x: 0,
    y,
    width: 160,
    height: 12,
    text,
    style: { fontSize: 8, bold: true, color: "#63758f", horizontalAlign: "left", verticalAlign: "top", lineHeight: 1.1 },
  };
}

function nativeField(id: string, binding: string, y: number, format?: string): JsonRecord {
  return {
    id,
    type: "field",
    x: 0,
    y,
    width: 160,
    height: 18,
    binding,
    format,
    style: { fontSize: 10, bold: true, color: "#23324a", horizontalAlign: "left", verticalAlign: "top", lineHeight: 1.2 },
  };
}

function distribute(elements: JsonRecord[], positionKey: "x" | "y", sizeKey: "width" | "height") {
  const sorted = [...elements].sort((a, b) => Number(a[positionKey] || 0) - Number(b[positionKey] || 0));
  if (sorted.length < 3) return;
  const start = Number(sorted[0][positionKey] || 0);
  const endElement = sorted[sorted.length - 1];
  const end = Number(endElement[positionKey] || 0) + Number(endElement[sizeKey] || 0);
  const totalSize = sorted.reduce((sum, element) => sum + Number(element[sizeKey] || 0), 0);
  const gap = (end - start - totalSize) / (sorted.length - 1);
  let cursor = start;
  sorted.forEach((element) => {
    element[positionKey] = cursor;
    cursor += Number(element[sizeKey] || 0) + gap;
  });
}

function titleize(value: string): string {
  return value
    .replace(/[_-]+/g, " ")
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (letter) => letter.toUpperCase());
}
